import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Search, Sparkles } from "lucide-react";
import api, { resolveMediaUrl } from "@/lib/api";

export default function BlogListPage() {
  const [blogs, setBlogs] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [activeCat, setActiveCat] = useState(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const opacity = useTransform(scrollYProgress, [0, 0.9], [1, 0]);

  useEffect(() => {
    (async () => {
      try {
        const [b, c] = await Promise.all([api.get("/blogs", { params: { limit: 30 } }), api.get("/blogs/categories")]);
        setBlogs(b.data.items || []);
        setCats(c.data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const params = { limit: 30 };
        if (q) params.q = q;
        if (activeCat) params.category = activeCat;
        const { data } = await api.get("/blogs", { params });
        setBlogs(data.items || []);
      } catch {}
    }, 250);
    return () => clearTimeout(t);
  }, [q, activeCat]);

  const featured = blogs[0];
  const rest = blogs.slice(1);

  const titleLines = useMemo(
    () => ["Field notes on", "brand, growth &", "ai automation."],
    []
  );

  return (
    <div data-testid="blog-list-root" className="relative">
      {/* KINETIC HERO */}
      <section ref={heroRef} className="relative pt-40 pb-24 sm:pt-52 sm:pb-32 overflow-hidden">
        <motion.div
          style={{ y, opacity }}
          className="absolute inset-0 -z-10 pointer-events-none"
        >
          <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[110vw] max-w-[1400px] h-[70vh] rounded-full blur-3xl opacity-40 bg-[conic-gradient(from_120deg,#00F0FF_0%,#8A2BE2_25%,#FF007F_50%,#FF5E00_75%,#00F0FF_100%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(50rem_30rem_at_50%_50%,rgba(5,5,10,0.6),rgba(5,5,10,1))]" />
          <svg className="absolute inset-0 deco opacity-[0.08]" width="100%" height="100%">
            <defs>
              <pattern id="bh-grid" width="72" height="72" patternUnits="userSpaceOnUse">
                <path d="M 72 0 L 0 0 0 72" fill="none" stroke="white" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#bh-grid)" />
          </svg>
        </motion.div>

        <div className="container-x relative">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="overline mb-8"
          >
            <span className="inline-flex items-center gap-1.5"><Sparkles size={11} /> The Novarise Journal</span>
          </motion.p>

          {/* Masked line-by-line reveal — signature on-load moment */}
          <h1 className="text-[3.4rem] sm:text-[5rem] lg:text-[7rem] leading-[0.9] font-extrabold tracking-tight uppercase max-w-6xl">
            {titleLines.map((line, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span
                  initial={{ y: "110%", rotate: 3 }}
                  animate={{ y: "0%", rotate: 0 }}
                  transition={{ duration: 0.95, delay: 0.15 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                  className="block will-change-transform"
                >
                  {i === 2 ? (
                    <>ai <span className="text-gradient-warm">automation.</span></>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <div className="mt-14 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.9 }}
              className="max-w-xl text-white/60 leading-relaxed"
            >
              Playbooks, teardowns, and slow essays from the team engineering
              growth infrastructure for the next 10,000 ambitious businesses.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.05 }}
              className="relative w-full sm:w-80"
            >
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                data-testid="blog-search"
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search journal…"
                className="w-full pl-10 pr-4 py-3 rounded-full glass border border-white/10 focus:outline-none focus:border-neon-cyan/50 text-sm"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* SLOW EDITORIAL MARQUEE */}
      <section className="relative py-10 border-y border-white/[0.06] overflow-hidden">
        <div className="flex whitespace-nowrap marquee-track-slow gap-16">
          {[...Array(2)].flatMap((_, k) =>
            ["Brand", "Growth", "Automation", "AI Systems", "Playbooks", "Case Studies", "Frameworks", "Field Notes"].map((w, i) => (
              <span key={`${k}-${i}`} className="flex items-center gap-6 font-mono text-xl sm:text-2xl uppercase tracking-[0.16em] text-white/35">
                {w} <span className="text-neon-magenta">✦</span>
              </span>
            ))
          )}
        </div>
      </section>

      {/* CATEGORY FILTER */}
      {cats.length > 0 && (
        <section className="pt-14 pb-2">
          <div className="container-x flex flex-wrap gap-2">
            <button
              onClick={() => setActiveCat(null)}
              data-testid="cat-all"
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition ${
                !activeCat ? "bg-white text-bg-0" : "bg-white/5 text-white/70 hover:bg-white/10"
              }`}
            >
              All
            </button>
            {cats.map((c) => (
              <button
                key={c.name}
                onClick={() => setActiveCat(c.name === activeCat ? null : c.name)}
                data-testid={`cat-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition ${
                  activeCat === c.name ? "bg-white text-bg-0" : "bg-white/5 text-white/70 hover:bg-white/10"
                }`}
              >
                {c.name} <span className="opacity-50 ml-1">{c.count}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* CONTENT */}
      <section className="section pt-10">
        <div className="container-x">
          {loading ? (
            <div className="text-white/50 py-24 text-center">Loading journal…</div>
          ) : blogs.length === 0 ? (
            <div className="glass rounded-3xl p-14 text-center text-white/60">
              No posts yet. Come back soon.
            </div>
          ) : (
            <>
              {/* Featured — spotlight card */}
              {featured && (
                <FeaturedCard blog={featured} />
              )}

              {/* Grid of the rest */}
              {rest.length > 0 && (
                <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {rest.map((b, i) => (
                    <BlogCard key={b.id} blog={b} idx={i} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}

function FeaturedCard({ blog }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="group relative"
      data-testid={`blog-featured-${blog.id}`}
    >
      <Link to={`/blog/${blog.slug}`} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
        <div className="lg:col-span-7 relative overflow-hidden rounded-3xl border border-white/[0.06]">
          <div className="aspect-[16/10] relative bg-bg-2">
            {blog.featured_image ? (
              <img
                src={resolveMediaUrl(blog.featured_image)}
                alt={blog.featured_image_alt || blog.title}
                className="w-full h-full object-cover transition-transform duration-[1.2s] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-neon-purple/40 via-neon-magenta/30 to-neon-orange/40" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-bg-0/70 via-transparent to-transparent" />
            <span className="absolute top-5 left-5 inline-flex items-center gap-1.5 px-3 py-1 rounded-full glass-strong text-[10px] font-mono uppercase tracking-[0.18em]">
              <Sparkles size={10} /> Featured
            </span>
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="flex items-center gap-2 mb-4">
            {(blog.categories || []).slice(0, 2).map((c) => (
              <span key={c} className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/50 px-2 py-1 rounded-full bg-white/5 border border-white/10">
                {c}
              </span>
            ))}
            <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/40">
              {blog.reading_time} min read
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05] group-hover:text-gradient-warm transition-all">
            {blog.title}
          </h2>
          {blog.excerpt && <p className="mt-4 text-white/60 leading-relaxed">{blog.excerpt}</p>}
          <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold">
            Read the essay <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </div>
        </div>
      </Link>
    </motion.article>
  );
}

function BlogCard({ blog, idx }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-8%" }}
      transition={{ duration: 0.55, delay: Math.min(idx * 0.05, 0.35), ease: [0.22, 1, 0.36, 1] }}
      className="group"
      data-testid={`blog-card-${blog.id}`}
    >
      <Link to={`/blog/${blog.slug}`} className="block">
        <div className="relative aspect-[16/11] rounded-2xl overflow-hidden border border-white/[0.06] bg-bg-2">
          {blog.featured_image ? (
            <img
              src={resolveMediaUrl(blog.featured_image)}
              alt={blog.featured_image_alt || blog.title}
              className="w-full h-full object-cover transition-transform duration-[1.2s] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.05]"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-neon-cyan/30 via-neon-purple/30 to-neon-magenta/30" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-bg-0/50 to-transparent" />
        </div>
        <div className="mt-4 flex items-center gap-2">
          {(blog.categories || []).slice(0, 1).map((c) => (
            <span key={c} className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/60">
              {c}
            </span>
          ))}
          <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/35">
            · {blog.reading_time} min
          </span>
        </div>
        <h3 className="mt-2 text-xl font-bold tracking-tight leading-tight group-hover:text-gradient-neon transition-all">
          {blog.title}
        </h3>
        {blog.excerpt && <p className="mt-2 text-sm text-white/55 line-clamp-2">{blog.excerpt}</p>}
      </Link>
    </motion.article>
  );
}
